import { useMemo } from "react";
import { Product, ProductDetails, PricingModel } from "../../../api/catalog";
import { ProductAlert } from "./ProductAlerts";

export type UseProductAlertsResult = { alerts: ProductAlert[]; is_sellable: boolean };

export function useProductAlerts(
  product: Product | null,
  details: ProductDetails | null,
  pricingModel: PricingModel | null,
  itemPrices: Array<{ price_type: string; unit_price: number }>,
  deliverables: Array<{ is_customer_visible: boolean; deliverable_type?: "customer" | "supplier" }>,
  processMappings: Array<unknown>
): UseProductAlertsResult {
  return useMemo(() => {
    const alerts: ProductAlert[] = [];

    if (!product) return { alerts, is_sellable: false };

    const usage = (product as { product_usage?: string }).product_usage ?? "sold";
    const hasPrice = itemPrices.length > 0;
    const hasPricingModel = !!pricingModel;
    const isService = product.product_type?.startsWith("service") ?? false;
    const hasDeliverables = deliverables.length > 0;
    const hasProcess = processMappings.length > 0;
    const is_sellable =
      usage === "sold" &&
      hasPrice &&
      hasPricingModel &&
      (!isService || hasDeliverables);

    // Alert: Produit vendu sans prix (uniquement pour product_usage = sold)
    if (usage === "sold" && itemPrices.length === 0) {
      alerts.push({
        id: "no-prices",
        severity: "warning",
        message: "Aucun prix défini pour ce produit",
        details: "Ajoutez au moins un prix dans l'onglet Pricing pour que le produit soit achetable.",
      });
    }

    // Alert: Produit sans livrables visibles client (ne compte que les livrables type "customer")
    const customerDeliverables = deliverables.filter((d) => (d.deliverable_type ?? "customer") === "customer");
    if (deliverables.length > 0) {
      const hasCustomerVisible = customerDeliverables.some((d) => d.is_customer_visible);
      if (customerDeliverables.length > 0 && !hasCustomerVisible) {
        alerts.push({
          id: "no-customer-visible-deliverables",
          severity: "warning",
          message: "Aucun livrable visible pour le client",
          details: "Tous les livrables client sont marqués comme internes. Les clients ne verront aucun livrable pour ce produit.",
        });
      } else if (customerDeliverables.length === 0) {
        alerts.push({
          id: "no-customer-deliverables",
          severity: "info",
          message: "Aucun livrable client",
          details: "Seules des obligations fournisseur sont définies. Les clients ne verront aucun livrable pour ce produit.",
        });
      }
    } else if (isService) {
      alerts.push({
        id: "no-deliverables",
        severity: "info",
        message: "Aucun livrable défini",
        details: "Considérez ajouter des livrables dans l'onglet Livrables pour clarifier ce que le client recevra.",
      });
    }

    // Alert: ItemPrice incohérents (floor > public)
    const publicPrice = itemPrices.find((p) => p.price_type === "public");
    const floorPrice = itemPrices.find((p) => p.price_type === "floor");
    if (publicPrice && floorPrice && floorPrice.unit_price > publicPrice.unit_price) {
      alerts.push({
        id: "inconsistent-prices",
        severity: "error",
        message: "Prix incohérents : le prix plancher est supérieur au prix public",
        details: `Le prix plancher (${floorPrice.unit_price} CAD) ne peut pas être supérieur au prix public (${publicPrice.unit_price} CAD).`,
      });
    }

    // Alert: Produit actif sans description
    if (product.status === "active" && !product.description_md) {
      alerts.push({
        id: "no-description",
        severity: "warning",
        message: "Produit actif sans description",
        details: "Ajoutez une description pour améliorer la présentation du produit aux clients.",
      });
    }

    // Alert: Produit vendu sans modèle de tarification
    if (usage === "sold" && !pricingModel) {
      alerts.push({
        id: "missing-pricing-model",
        severity: "warning",
        message: "Définir un modèle de tarification",
        details: "Ce produit est vendu. Définissez un mode (prix fixe ou par quantité) et les prix dans l'onglet Pricing.",
      });
    }

    return { alerts, is_sellable };
  }, [product, details, pricingModel, itemPrices, deliverables, processMappings]);
}
